import {FiltersFromUrl} from 'plugins/modules/filter/helpers/FiltersFromUrl';
import {
    FilterValuesJoinType,
    IFilter,
    IFilterFacet,
    IFilterFacetVariant,
    TFilterRangeValue,
} from 'plugins/modules/filter/types';
import {isBrowserContext} from 'core/helpers';
import {FiltersToUrl} from 'plugins/modules/filter/helpers/FiltersToUrl';
import {FILTER_URL_SORT_PRIORITY_QUEUE} from 'plugins/modules/filter/constants';
import {AppliedFilters} from 'plugins/modules/filter/helpers/BrowserFilters/AppliedFilters';
import {getFilterValuesJoinType} from 'plugins/modules/filter/helpers';

export interface IUserAddFilterRangeValues {
    code: string;
    values: TFilterRangeValue;
}

export interface IUserAddFilterValueProps {
    code: string;
    value: string;
    valuesJoinType: FilterValuesJoinType;
}

export class BrowserFilters {
    private static instance: BrowserFilters;
    private appliedFiltersInstance: AppliedFilters;
    private currentFilters: IFilter[] = [];

    private constructor() {
        if (!isBrowserContext()) {
            throw new Error('Данный класс предназначен для работы только в браузере!');
        }

        this.appliedFiltersInstance = AppliedFilters.getInstance();
        this.setCurrentUrlFilters();
    }

    static getInstance = () => {
        if (!BrowserFilters.instance) {
            BrowserFilters.instance = new BrowserFilters();
        }

        return BrowserFilters.instance;
    };

    private addFilterRangeValues = ({code, values}: IUserAddFilterRangeValues) => {
        const [min, max] = values;

        if (!min && !max) {
            this.appliedFiltersInstance.removeAppliedFilter(code, null);
            this.removeFilterByCode(code);

            return;
        }

        this.appliedFiltersInstance.addAppliedFilter(code, null);

        const currentFilter = this.currentFilters.find((filter) => filter.code === code);

        if (!currentFilter) {
            this.currentFilters.push({
                code,
                values,
                valuesJoinType: FilterValuesJoinType.RANGE,
            });

            this.sortCurrentFilters();

            return;
        }

        currentFilter.values = values;
        currentFilter.valuesJoinType = FilterValuesJoinType.RANGE;
    };

    private addOneFilterValue = ({code, value, valuesJoinType}: IUserAddFilterValueProps) => {
        const currentFilter = this.currentFilters.find((filter) => filter.code === code);

        if (!currentFilter) {
            this.currentFilters.push({
                code,
                values: [value],
                valuesJoinType,
            });

            this.sortCurrentFilters();

            return;
        }

        const isValueAlreadyExists = currentFilter.values.includes(value);

        if (isValueAlreadyExists) {
            return;
        }

        currentFilter.values.push(value);
        currentFilter.values.sort();
        currentFilter.valuesJoinType = valuesJoinType;
    };

    private getReturnData = () => {
        const {pathName} = new FiltersToUrl(this.currentFilters);

        return {
            pathName,
        };
    };

    private removeAllFilters = () => {
        this.currentFilters = [];
    };

    private removeFilterByCode = (filterCode: string) => {
        this.currentFilters = this.currentFilters.filter((item) => item.code !== filterCode);
    };

    private removeOneFilterValue = ({code, value, valuesJoinType}: IUserAddFilterValueProps) => {
        const currentFilter = this.currentFilters.find((filter) => filter.code === code);

        if (!currentFilter) {
            return;
        }

        const isValueExists = currentFilter.values.includes(value);

        if (!isValueExists) {
            return;
        }

        if (1 === currentFilter.values.length) {
            this.removeFilterByCode(code);

            return;
        }

        currentFilter.values = currentFilter.values.filter((item) => item !== value);
        currentFilter.valuesJoinType = valuesJoinType;
    };

    private setCurrentUrlFilters = () => {
        const {filters} = new FiltersFromUrl(window.location.pathname);

        this.currentFilters = filters;
    };

    private sortCurrentFilters = () => {
        this.currentFilters.sort((a, b) => {
            const codeA = a.code;
            const codeB = b.code;
            const priorityA = FILTER_URL_SORT_PRIORITY_QUEUE.indexOf(codeA);
            const priorityB = FILTER_URL_SORT_PRIORITY_QUEUE.indexOf(codeB);

            // если коды есть в приоритетной очереди, то смотрим на приоритет
            if (-1 !== priorityA && -1 !== priorityB) {
                return priorityA - priorityB;
            }

            if (-1 !== priorityA) {
                return -1;
            }

            if (-1 !== priorityB) {
                return 1;
            }

            // если кодов нет в приоритетной очереди, то выполняем обычную сортировку
            if (codeA < codeB) {
                return -1;
            }

            if (codeA > codeB) {
                return 1;
            }

            return 0;
        });
    };

    getAppliedFilters = (filters: IFilterFacet[]) => {
        return this.appliedFiltersInstance.getAppliedFilters(this.currentFilters, filters);
    };

    updateCurrentFiltersFromUrl = () => {
        this.setCurrentUrlFilters();
        this.appliedFiltersInstance.updateAppliedFiltersOrder(this.currentFilters);
    };

    userAddFilterRangeValues = (filterData: IUserAddFilterRangeValues) => {
        this.addFilterRangeValues(filterData);

        return this.getReturnData();
    };

    userAddOneFilterValue = (filter: IFilterFacet, variantCode: IFilterFacetVariant['code']) => {
        const {code: filterCode} = filter;

        this.appliedFiltersInstance.addAppliedFilter(filterCode, String(variantCode));
        this.addOneFilterValue({
            code: filterCode,
            value: String(variantCode),
            valuesJoinType: getFilterValuesJoinType(filter),
        });

        return this.getReturnData();
    };

    userRemoveAllFilters = () => {
        this.appliedFiltersInstance.removeAllAppliedFilters();
        this.removeAllFilters();

        return this.getReturnData();
    };

    userRemoveFilterByCode = (filterCode: string, variantCode: string | null = null) => {
        this.appliedFiltersInstance.removeAppliedFilter(filterCode, variantCode);
        this.removeFilterByCode(filterCode);

        return this.getReturnData();
    };

    userRemoveOneFilterValue = (filter: IFilterFacet, variantCode: IFilterFacetVariant['code']) => {
        const {code: filterCode} = filter;

        this.appliedFiltersInstance.removeAppliedFilter(filterCode, String(variantCode));
        this.removeOneFilterValue({
            code: filterCode,
            value: String(variantCode),
            valuesJoinType: getFilterValuesJoinType(filter),
        });

        return this.getReturnData();
    };
}
